<template>
  <div class="keypad">
    <div v-if="store.isScientific" class="sci-toolbar">
      <span class="sci-toolbar__title">Mode scientifique</span>
      <button
        class="angle-chip"
        type="button"
        :aria-label="'Unite d\'angle : ' + store.angleMode"
        @click="store.toggleAngleMode()"
      >
        {{ store.angleMode }}
      </button>
    </div>
    <!-- Panneau scientifique avec toutes les fonctions visibles par sections -->
    <transition name="sci">
      <div v-if="store.isScientific" class="keypad__sci">
        <section class="sci-section sci-section--memory">
          <h3 class="sci-section__title">Memoire</h3>
          <div class="memory-row" aria-label="Memoire de calcul">
            <button type="button" @click="store.memoryClear">MC</button>
            <button
              type="button"
              :disabled="!store.hasMemory"
              @click="store.memoryRecall"
            >
              MR
            </button>
            <button type="button" @click="store.memoryAdd">M+</button>
            <button type="button" @click="store.memorySubtract">M-</button>
          </div>
        </section>

        <section
          v-for="section in scientificSections"
          :key="section.id"
          class="sci-section"
        >
          <h3 class="sci-section__title">{{ section.title }}</h3>
          <div class="sci-section__keys">
            <CalcButton
              v-for="key in section.keys"
              :key="key.label"
              :label="key.label"
              variant="special"
              :aria-label="key.aria"
              @press="() => press(key)"
            />
          </div>
        </section>

        <section class="sci-section sci-section--analysis">
          <h3 class="sci-section__title">Analyse scientifique</h3>
          <CalcAnalysis />
        </section>
      </div>
    </transition>
    <!-- Pavé principal -->
    <div class="keypad__main">
      <CalcButton
        v-for="key in mainKeys"
        :key="key.label"
        :label="labelFor(key)"
        :variant="key.variant"
        :span="key.span || 0"
        :aria-label="key.aria"
        @press="() => press(key)"
      />
    </div>
  </div>
</template>

<script setup>
import { defineAsyncComponent } from 'vue';
import { useCalculator } from '../stores/calculator.js';
import CalcButton from './CalcButton.vue';
const CalcAnalysis = defineAsyncComponent(() => import('./CalcAnalysis.vue'));

const store = useCalculator();

// Chaque touche décrit SON action de façon déclarative - plus aucune
// duplication entre modes, plus aucun risque de désynchronisation.
const scientificKeys = [
  { label: 'sin', insert: 'sin(', aria: 'sinus' },
  { label: 'cos', insert: 'cos(', aria: 'cosinus' },
  { label: 'tan', insert: 'tan(', aria: 'tangente' },
  { label: 'ln', insert: 'ln(', aria: 'logarithme népérien' },
  { label: 'log', insert: 'log10(', aria: 'logarithme base 10' },
  { label: '√', insert: 'sqrt(', aria: 'racine carrée' },
  { label: 'x²', insert: '^2', aria: 'au carré' },
  { label: 'xʸ', insert: '^', aria: 'puissance' },
  { label: 'π', insert: 'pi', aria: 'pi' },
  { label: 'e', insert: 'e', aria: 'nombre e' },
  { label: '(', insert: '(', aria: 'parenthèse ouvrante' },
  { label: ')', insert: ')', aria: 'parenthèse fermante' },
];

const scientificAdvancedKeys = [
  { label: 'asin', insert: 'asin(', aria: 'arc sinus' },
  { label: 'acos', insert: 'acos(', aria: 'arc cosinus' },
  { label: 'atan', insert: 'atan(', aria: 'arc tangente' },
  { label: 'sinh', insert: 'sinh(', aria: 'sinus hyperbolique' },
  { label: 'cosh', insert: 'cosh(', aria: 'cosinus hyperbolique' },
  { label: 'tanh', insert: 'tanh(', aria: 'tangente hyperbolique' },
  { label: 'asinh', insert: 'asinh(', aria: 'arc sinus hyperbolique' },
  { label: 'acosh', insert: 'acosh(', aria: 'arc cosinus hyperbolique' },
  { label: 'atanh', insert: 'atanh(', aria: 'arc tangente hyperbolique' },
  { label: 'sec', insert: 'sec(', aria: 'secante' },
  { label: 'csc', insert: 'csc(', aria: 'cosecante' },
  { label: 'cot', insert: 'cot(', aria: 'cotangente' },
  { label: 'log2', insert: 'log2(', aria: 'logarithme base 2' },
  { label: '10^x', insert: '10^(', aria: 'dix puissance x' },
  { label: 'e^x', insert: 'e^(', aria: 'exponentielle' },
  { label: 'exp', insert: 'exp(', aria: 'exponentielle' },
  { label: 'cbrt', insert: 'cbrt(', aria: 'racine cubique' },
  { label: '1/x', insert: '1/(', aria: 'inverse' },
  { label: 'n!', insert: '!', aria: 'factorielle' },
  { label: 'nCr', insert: 'combinations(', aria: 'combinaisons' },
  { label: 'nPr', insert: 'permutations(', aria: 'arrangements' },
  { label: 'abs', insert: 'abs(', aria: 'valeur absolue' },
  { label: 'floor', insert: 'floor(', aria: 'partie entiere inferieure' },
  { label: 'ceil', insert: 'ceil(', aria: 'partie entiere superieure' },
  { label: 'round', insert: 'round(', aria: 'arrondi' },
  { label: 'mod', insert: 'mod(', aria: 'modulo' },
  { label: 'gcd', insert: 'gcd(', aria: 'plus grand diviseur commun' },
  { label: 'lcm', insert: 'lcm(', aria: 'plus petit multiple commun' },
  { label: 'phi', insert: 'phi', aria: 'nombre phi' },
  { label: 'i', insert: 'i', aria: 'unite imaginaire' },
  { label: 'Ans', insert: 'ans', aria: 'dernier resultat' },
];

const scientificSections = [
  {
    id: 'trig',
    title: 'Trigonometrie',
    keys: [
      ...scientificKeys.slice(0, 3),
      ...scientificAdvancedKeys.slice(0, 3),
      ...scientificAdvancedKeys.slice(9, 12),
    ],
  },
  {
    id: 'hyper',
    title: 'Fonctions hyperboliques',
    keys: scientificAdvancedKeys.slice(3, 9),
  },
  {
    id: 'log-powers',
    title: 'Logarithmes et puissances',
    keys: [
      ...scientificKeys.slice(3, 5),
      ...scientificAdvancedKeys.slice(12, 16),
      ...scientificKeys.slice(6, 8),
    ],
  },
  {
    id: 'roots-rounding',
    title: 'Racines et arrondis',
    keys: [
      scientificKeys[5],
      scientificAdvancedKeys[16],
      ...scientificAdvancedKeys.slice(21, 25),
    ],
  },
  {
    id: 'combinatorics',
    title: 'Combinatoire et operateurs',
    keys: [
      ...scientificAdvancedKeys.slice(17, 21),
      ...scientificAdvancedKeys.slice(25, 28),
    ],
  },
  {
    id: 'constants',
    title: 'Constantes et symboles',
    keys: [
      ...scientificKeys.slice(8, 10),
      ...scientificAdvancedKeys.slice(28, 31),
    ],
  },
  {
    id: 'syntax',
    title: 'Syntaxe',
    keys: scientificKeys.slice(10, 12),
  },
];
const mainKeys = [
  { label: 'AC', action: 'clear', variant: 'clear', aria: 'tout effacer' },
  { label: '±', action: 'negate', variant: 'operator', aria: 'signe' },
  { label: '%', insert: '%', variant: 'operator', aria: 'pourcentage' },
  { label: '÷', insert: '÷', variant: 'operator', aria: 'diviser' },

  { label: '7', insert: '7' },
  { label: '8', insert: '8' },
  { label: '9', insert: '9' },
  { label: '×', insert: '×', variant: 'operator', aria: 'multiplier' },

  { label: '4', insert: '4' },
  { label: '5', insert: '5' },
  { label: '6', insert: '6' },
  { label: '−', insert: '-', variant: 'operator', aria: 'soustraire' },

  { label: '1', insert: '1' },
  { label: '2', insert: '2' },
  { label: '3', insert: '3' },
  { label: '+', insert: '+', variant: 'operator', aria: 'additionner' },

  {
    label: 'Sci',
    action: 'toggleSci',
    variant: 'accent',
    aria: 'mode scientifique',
  },
  { label: '0', insert: '0' },
  { label: '.', insert: '.' },
  { label: '=', action: 'compute', variant: 'equals', aria: 'égal' },
];

function labelFor(key) {
  // Le bouton AC devient C quand il y a de la saisie.
  if (key.action === 'clear') {
    return store.hasInput || store.result ? 'C' : 'AC';
  }
  return key.label;
}

function press(key) {
  if (key.insert !== undefined) {
    store.append(key.insert);
    return;
  }
  switch (key.action) {
    case 'clear':
      store.clear();
      break;
    case 'compute':
      store.compute();
      break;
    case 'toggleSci':
      store.toggleScientific();
      break;
    case 'negate':
      negate();
      break;
  }
}

// Inverse le signe de l'expression courante en l'enveloppant dans -(...).
function negate() {
  const cur = store.input || store.result;
  if (!cur) return;
  if (cur.startsWith('-(') && cur.endsWith(')')) {
    store.setInput(cur.slice(2, -1));
  } else {
    store.setInput(`-(${cur})`);
  }
}
</script>

<style scoped>
.keypad {
  position: relative;
  margin-top: var(--gap);
}

.sci-toolbar {
  position: absolute;
  z-index: 21;
  top: 0;
  left: calc(100% + var(--gap));
  width: 380px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
  padding: 0 2px;
}

.sci-toolbar__title {
  color: var(--text-dim);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.angle-chip {
  border: 1px solid color-mix(in srgb, var(--accent) 42%, var(--border));
  border-radius: 999px;
  padding: 5px 10px;
  background: color-mix(in srgb, var(--accent) 13%, transparent);
  color: var(--accent);
  font: 700 0.72rem var(--font-mono);
  letter-spacing: 0.08em;
  cursor: pointer;
  transition:
    background var(--transition),
    transform 120ms ease;
}

.angle-chip:hover {
  background: color-mix(in srgb, var(--accent) 24%, transparent);
}

.angle-chip:active {
  transform: scale(0.95);
}

.angle-chip:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.keypad__sci {
  position: absolute;
  z-index: 20;
  top: 0;
  left: calc(100% + var(--gap));
  width: 380px;
  max-height: min(74vh, 720px);
  padding: 44px 10px 12px;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--surface-solid);
  box-shadow: var(--shadow);
  backdrop-filter: blur(18px);
  display: grid;
  gap: 14px;
  margin-bottom: var(--gap);
  overflow-x: hidden;
}

.sci-section {
  display: grid;
  gap: 7px;
  padding-top: 10px;
  border-top: 1px solid var(--border);
}

.sci-section:first-child {
  padding-top: 0;
  border-top: 0;
}

.sci-section__title {
  margin: 0;
  color: var(--text-dim);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.sci-section__keys {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--gap);
}

.sci-section--analysis {
  gap: 8px;
}

.sci-section--analysis :deep(.analysis) {
  padding: 0;
}

.keypad__main {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--gap);
}

.memory-row {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

.memory-row button {
  border: 1px solid color-mix(in srgb, var(--accent) 28%, var(--border));
  border-radius: 9px;
  padding: 6px 4px;
  background: color-mix(in srgb, var(--accent) 8%, transparent);
  color: var(--accent);
  cursor: pointer;
  font: 700 0.72rem var(--font-mono);
}

.memory-row button:hover {
  background: color-mix(in srgb, var(--accent) 20%, transparent);
}

.memory-row button:disabled {
  cursor: not-allowed;
  opacity: 0.35;
}

.keypad__sci :deep(.btn) {
  height: 48px;
  font-size: 0.9rem;
}

/* Animation de repli du panneau scientifique */
.sci-enter-active,
.sci-leave-active {
  transition:
    max-height 300ms ease,
    opacity 300ms ease,
    margin 300ms ease;
  max-height: 900px;
}
.sci-enter-from,
.sci-leave-to {
  max-height: 0;
  opacity: 0;
  margin-bottom: 0;
}

@media (max-width: 1100px) {
  .sci-toolbar,
  .keypad__sci {
    position: relative;
    top: auto;
    left: auto;
    width: 100%;
  }

  .keypad__sci {
    max-height: min(55vh, 520px);
    padding: 12px;
  }

  .sci-section__keys {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 420px) {
  .keypad__sci {
    gap: 8px;
  }
  .keypad__main {
    gap: 8px;
  }
}
</style>
